/**
 * Feedback modal prototype - scenes including screenshot staging
 * Access: http://flownotes.localhost/?test=feedback-modal
 */

import { useState } from 'react'
import { Flag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FeedbackModal } from '@/components/features/chats/FeedbackModal'
import type { ChatFeedbackCategory } from '@/lib/chat-feedback'

const MOCK_DETAIL = {
  category: 'agent-issue' as ChatFeedbackCategory,
  conversationId: 'cedbf433-14b1-47f9-8705-2952432778f1',
  threadId: 'thread-mock-001',
  model: 'claude-sonnet-4',
  provider: 'anthropic',
}

const MOCK_MESSAGES = [
  { role: 'user', content: 'Can you ingest this Basecamp message thread?' },
  { role: 'assistant', content: 'I will search for the message and then call ingest...' },
  { role: 'user', content: 'The chat just narrated everything - no progress cards.' },
  { role: 'assistant', content: 'I apologize for the confusion. Let me try again...' },
]

/** Tiny 1x1 PNG data URLs so the screenshots scene needs no network. */
const PROTOTYPE_SCREENSHOT_A =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='
const PROTOTYPE_SCREENSHOT_B =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='

type Scene = 'idle' | 'filled' | 'filing' | 'success' | 'error' | 'mobile' | 'screenshots'

const SCENES: { id: Scene; label: string }[] = [
  { id: 'idle', label: 'Idle' },
  { id: 'filled', label: 'Filled' },
  { id: 'screenshots', label: 'Screenshots' },
  { id: 'filing', label: 'Filing' },
  { id: 'success', label: 'Success' },
  { id: 'error', label: 'Error' },
  { id: 'mobile', label: 'Mobile' },
]

/** Prototype harness for FeedbackModal scenes at ?test=feedback-modal. */
export function TestFeedbackModal() {
  const [scene, setScene] = useState<Scene>('idle')
  const [open, setOpen] = useState(true)

  const prototypeScene =
    scene === 'idle' ? null : scene

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <Flag className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Feedback modal prototype</h1>
            <p className="text-sm text-muted-foreground">
              Scenes for Tier 1 pilot feedback modal (?test=feedback-modal)
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {SCENES.map((s) => (
            <Button
              key={s.id}
              size="sm"
              variant={scene === s.id ? 'default' : 'outline'}
              onClick={() => {
                setScene(s.id)
                setOpen(true)
              }}
            >
              {s.label}
            </Button>
          ))}
        </div>

        <Button variant="secondary" onClick={() => setOpen(true)}>
          Reopen modal
        </Button>

        <FeedbackModal
          open={open}
          onOpenChange={setOpen}
          initialDetail={scene === 'idle' ? {} : MOCK_DETAIL}
          messages={scene === 'idle' ? [] : MOCK_MESSAGES}
          conversationTitle={
            scene === 'filled' || scene === 'screenshots'
              ? 'Basecamp ingest narration issue'
              : null
          }
          workspaceScope={['hop-studios']}
          prototypeScene={prototypeScene}
          prototypeSuccessPath="hop-studios/flownotes-pilot/feedback/2026-06-20-chat-narration-issue.md"
          prototypeErrorMessage="503: database unavailable"
          prototypeStagedScreenshots={
            scene === 'screenshots'
              ? [
                  {
                    id: 'proto-shot-1',
                    previewUrl: PROTOTYPE_SCREENSHOT_A,
                    name: 'chat-narration.png',
                  },
                  {
                    id: 'proto-shot-2',
                    previewUrl: PROTOTYPE_SCREENSHOT_B,
                    name: 'missing-cards.png',
                  },
                ]
              : undefined
          }
        />
      </div>
    </div>
  )
}
